import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

// Detailed weekly-progress block: 3 header cards (weekly %, stagnated, declined)
// + rankings of the top declined / stagnated exercises. Extracted from the old
// DashboardPage so it can live in ProgressPage without duplicating logic.
const ProgressStatsBlock = ({ progress }) => {
  const { t } = useTranslation();

  const {
    weeklyProgressPercent = 0,
    improvedExercises = 0,
    declinedExercises = 0,
    totalComparedExercises = 0,
    declined = [],
    stagnated = [],
    stagnatedTotal = 0,
  } = progress || {};

  const noData = totalComparedExercises === 0 && stagnatedTotal === 0;

  return (
    <>
      <h2 className="progress-section-title">
        <TrendingUp size={18} />
        {t('dashboard.progress_section_title')}
      </h2>

      <div className="progress-grid">
        <div className={`progress-card ${totalComparedExercises > 0 ? 'pc-improved' : 'pc-stable'}`}>
          <span className="pc-label">
            <TrendingUp size={12} /> {t('dashboard.progress_card_weekly')}
          </span>
          <span className="pc-value">{Math.round(weeklyProgressPercent)}%</span>
          <span className="pc-sub">
            {improvedExercises}/{totalComparedExercises} · {t('dashboard.progress_card_weekly_desc')}
          </span>
        </div>

        <div className={`progress-card ${stagnatedTotal > 0 ? 'pc-stagnated' : 'pc-stable'}`}>
          <span className="pc-label">
            <AlertTriangle size={12} /> {t('dashboard.progress_card_stagnated')}
          </span>
          <span className="pc-value">{stagnatedTotal}</span>
          <span className="pc-sub">
            {stagnatedTotal > 0
              ? t('dashboard.progress_card_stagnated_unit')
              : t('dashboard.progress_card_stagnated_empty')}
          </span>
        </div>

        <div className={`progress-card ${declinedExercises > 0 ? 'pc-declined' : 'pc-stable'}`}>
          <span className="pc-label">
            <TrendingDown size={12} /> {t('dashboard.progress_card_decline')}
          </span>
          <span className="pc-value">{declinedExercises}</span>
          <span className="pc-sub">
            {declinedExercises > 0
              ? t('dashboard.progress_card_decline_unit')
              : t('dashboard.progress_card_decline_empty')}
          </span>
        </div>
      </div>

      {noData ? (
        <div className="progress-empty">{t('dashboard.progress_no_comparison')}</div>
      ) : (
        <>
          {declined.length > 0 && (
            <div className="ranking-section">
              <h3>{t('dashboard.progress_top_declined')}</h3>
              <div className="ranking-list">
                {declined.map((item, idx) => (
                  <div key={`dec-${idx}`} className="ranking-item is-declined">
                    <div className="ri-name">
                      <span className="ri-text">{item.exercise}</span>
                    </div>
                    <span className="ri-value">↓ {item.progress.toFixed(1)}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {stagnated.length > 0 && (
            <div className="ranking-section">
              <h3>{t('dashboard.progress_stagnated')}</h3>
              <div className="ranking-list">
                {stagnated.map((item, idx) => (
                  <div key={`stg-${idx}`} className="ranking-item is-stagnated">
                    <div className="ri-name">
                      <span className="ri-text">{item.exercise}</span>
                    </div>
                    <span className="ri-value">
                      {t('dashboard.progress_weeks_short', { count: item.weeks })}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default ProgressStatsBlock;
